import { useCallback, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { Plus, Trash2 } from "lucide-react"
import type { AppShell } from "@/App"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { listBrands, listSiteKits, removeSiteKit, setSiteKit, type SiteKitRow } from "@/lib/api"
import { formatEur } from "@/lib/format"
import { KIND_LABEL_PT, detailPathForKind } from "@/lib/kind-labels"

/**
 * «Kits do site» (E4, 02/11) — a composição de cada kit vendido no site
 * (KITSTANDARD, KITSTANDARDPLUS…) é uma **proposta-modelo**: monta-se no
 * simulador como qualquer simulação (marca, painéis, inversor, bateria e o
 * «Editar Kit» para acertar linhas e mão de obra) e associa-se aqui ao kit.
 * Os pedidos do site com esse kit saem com a marca, os artigos e as
 * quantidades do modelo, ao preço do site; o carregador pedido à parte soma.
 * As propostas-modelo não aparecem na lista nem nos Relatórios.
 */
export function SiteKitsTab({ shell }: { shell: AppShell | null }) {
  const [rows, setRows] = useState<SiteKitRow[] | null>(null)
  const [brands, setBrands] = useState<Map<number, string>>(new Map())
  const [error, setError] = useState<string | null>(null)
  const [kitRef, setKitRef] = useState("")
  const [number, setNumber] = useState("")
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    if (!shell) return
    try {
      setRows(await listSiteKits(shell))
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }, [shell])

  useEffect(() => {
    void load()
    if (!shell) return
    listBrands(shell)
      .then((bs) => setBrands(new Map(bs.map((b) => [Number(b.brand_id), b.brand_name]))))
      .catch(() => undefined)
  }, [shell, load])

  const toast = (variant: "success" | "error", title: string, description?: string) =>
    shell?.bridge.toast({ variant, title, ...(description ? { description } : {}) })

  const add = async () => {
    if (!shell || !kitRef.trim() || !number.trim()) return
    setBusy(true)
    try {
      const r = await setSiteKit(shell, kitRef, number)
      toast("success", `${r.kit_ref} → ${r.number}`)
      setKitRef("")
      setNumber("")
      await load()
    } catch (e) {
      toast("error", "Não foi possível associar", e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const remove = async (ref: string) => {
    if (!shell) return
    setBusy(true)
    try {
      await removeSiteKit(shell, ref)
      toast("success", `${ref} sem modelo`)
      await load()
    } catch (e) {
      toast("error", "Não foi possível remover", e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  const sorted = useMemo(() => rows ?? [], [rows])

  if (error) return <p className="text-sm text-destructive">{error}</p>

  return (
    <Card>
      <CardHeader>
        <CardTitle>Kits do site</CardTitle>
        <p className="text-sm text-muted-foreground">
          Monte cada kit no simulador como uma simulação normal (marca, equipamento e «Editar Kit» para as linhas e a
          mão de obra) e associe-a aqui à referência do kit no site. Os pedidos do site com esse kit saem com esta
          composição ao preço do site; um kit sem modelo sai pela recomendação do simulador, com a «Marca dos kits do
          site» (Solar → Sistema FV), e fica para revisão.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs" htmlFor="site-kit-ref">Referência do kit</Label>
            <Input id="site-kit-ref" className="w-48" placeholder="KITSTANDARD" value={kitRef} onChange={(e) => setKitRef(e.target.value.toUpperCase())} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs" htmlFor="site-kit-number">Proposta-modelo (número)</Label>
            <Input id="site-kit-number" className="w-48" placeholder="SOL-2026-1101/1" value={number} onChange={(e) => setNumber(e.target.value)} />
          </div>
          <Button onClick={add} disabled={busy || !kitRef.trim() || !number.trim()}>
            <Plus className="mr-1 h-4 w-4" />
            Associar
          </Button>
        </div>

        {rows == null ? (
          <p className="text-sm text-muted-foreground">A carregar…</p>
        ) : sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nenhum kit tem modelo — os pedidos com kit saem pela recomendação do simulador.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kit</TableHead>
                <TableHead>Proposta-modelo</TableHead>
                <TableHead>Módulo</TableHead>
                <TableHead>Marca</TableHead>
                <TableHead className="text-right">Linhas</TableHead>
                <TableHead className="text-right">Total do modelo</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sorted.map((r) => {
                const to = detailPathForKind(r.kind, r.proposal_id)
                return (
                  <TableRow key={r.kit_ref}>
                    <TableCell className="font-medium">{r.kit_ref}</TableCell>
                    <TableCell>
                      {to ? <Link to={to} className="text-accent hover:underline">{r.number}</Link> : r.number}
                      {r.client_name && <span className="ml-2 text-muted-foreground">{r.client_name}</span>}
                    </TableCell>
                    <TableCell>{KIND_LABEL_PT[r.kind] ?? r.kind}</TableCell>
                    <TableCell>{r.brand_id != null ? (brands.get(r.brand_id) ?? `#${r.brand_id}`) : "—"}</TableCell>
                    <TableCell className="text-right">{r.lines}</TableCell>
                    <TableCell className="text-right">{r.total_price_eur != null ? formatEur(r.total_price_eur) : "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" disabled={busy} onClick={() => remove(r.kit_ref)} aria-label={`Remover o modelo do ${r.kit_ref}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}
